import { createTheme, PartialTheme } from "@fluentui/react/lib/Theme";

import { getJSON, tryGetJSON } from "API/JSON";
import { IAddress } from "Model/Configuration/IAddress";
import { IAuth } from "Model/Configuration/IAuth";
import { IConfiguration } from "Model/Configuration/IConfiguration";
import { IFieldMetadata } from "Model/Configuration/IFieldMetadata";
import { IFilterCategory } from "Model/Configuration/IFilterCategories";
import { IMergeField } from "Model/Configuration/IMergeFields";
import { IOptionList } from "Model/Configuration/IOptions";
import { IOverviewColumn } from "Model/Configuration/IOverviewColumns";
import { ThemeType } from "Model/Configuration/ITheme";
import { IToolbarItem } from "Model/Configuration/IToolbarItem";
import { EntityType } from "Model/EntityType";
import {
  hasElement, hasElements, hasNoElements, toSafeArray
} from "./Array";
import { isFalse, isTrue } from "./Boolean";
import { deepAssign, isEmpty } from "./Object";
import { isNullOrWhiteSpace } from "./String";

const DC_MERGE_FIELDS_REQUIRED = {
  company: [`name`, `addressLine`, `city`, `postalCode`, `country`],
  contact: [`firstName`, `lastName`],
  project: [`name`, `addressLine`, `city`, `postalCode`, `country`]
};

export class Configuration {
  private loadConfig: Promise<Partial<IConfiguration>[]> = null;

  private rawConfig: Partial<IConfiguration> = null;

  public async initialize(): Promise<void> {
    if (this.loadConfig == null) {
      this.loadConfig = Promise.all([
        Configuration.loadConfigFile(`config/Configuration.default.json`, `config/custom/Configuration.json`),
        Configuration.loadConfigFile(`config/Authentication.default.json`, `config/custom/Authentication.json`),
        Configuration.loadConfigFile(`config/Environment.default.json`, `config/custom/Environment.json`)
      ]);
    }

    const [auth, config, environment] = await this.loadConfig;
    this.rawConfig = { ...auth, ...config, ...environment };
    this.setEnvironmentName();
    Configuration.instance = this;
  }

  private static async loadConfigFile(defaultUrl: string, customUrl: string): Promise<Partial<IConfiguration>> {
    const defaultJson = await getJSON<IConfiguration>(defaultUrl);
    const customJson = await tryGetJSON<IConfiguration>(customUrl, `ℹ️ "${customUrl}" is optional.`);

    return deepAssign(defaultJson, customJson);
  }

  public get isInitialized(): boolean {
    return this.rawConfig != null;
  }

  public getBackendBaseUrl(): string {
    return this.rawConfig?.backendBaseUrl ?? `http://localhost:3002`;
  }

  public getOptionValues(fieldName: string, type: EntityType): IOptionList {
    const metadata = this.getMetadata(fieldName, type);
    if (hasNoElements(metadata.options)) {
      return [];
    }

    return metadata.options;
  }

  public getAuth(): IAuth {
    return this.rawConfig?.authentication;
  }

  public getFilterFieldNames(): string[] {
    return this.rawConfig?.filterFieldNames ?? [`name`];
  }

  public getOverviewColumns(): IOverviewColumn[] {
    const overviewColumns = this.rawConfig?.overviewColumns;
    if (hasNoElements(overviewColumns)) {
      return [];
    }

    const fieldNameList = overviewColumns.map(({ fieldName }) => fieldName);
    const fieldMetadataList = this.fieldListWithMetadata(fieldNameList, EntityType.Project);

    return overviewColumns.map((column, index) => ({
      ...fieldMetadataList[index],
      ...column,
      isSorted: column.fieldName === `@createdOn` || column.fieldName === `@modifiedOn`,
      isSortedDescending: column.fieldName === `@createdOn` || column.fieldName === `@modifiedOn`
    }));
  }

  public getDetailsProject(dataSource: string = null): IFieldMetadata[][] {
    return this.getDetails(this.rawConfig?.detail?.project, EntityType.Project, dataSource);
  }

  public getDetailsCompany(dataSource: string = null): IFieldMetadata[][] {
    return this.getDetails(this.rawConfig?.detail?.company, EntityType.Company, dataSource);
  }

  public getDetailsContact(dataSource: string = null): IFieldMetadata[][] {
    return this.getDetails(this.rawConfig?.detail?.contact, EntityType.Contact, dataSource);
  }

  private getDetails(fieldGroups: string[][], type: EntityType, dataSource: string = null): IFieldMetadata[][] {
    if (hasNoElements(fieldGroups)) {
      return [];
    }

    return this.fieldGroupsWithMetadata(fieldGroups, type, dataSource).map((group) => group.slice(0, 6));
  }

  private excludeEditFields = [
    `@avLastCheckedOn`,
    `@createdOn`,
    `@dcLastCheckedOn`,
    `@exportedOn`,
    `@id`,
    `@modifiedOn`,
    `@rawData`,
    `@relevantCount`,
    `@status`,
    `@targetId`,
    `@targetUrl`,
    `dataSourceId`,
    `dataSourceUrl`,
    `dataSource`
  ];

  public getEditProject(dataSource: string = null): IFieldMetadata[][] {
    return this.fieldGroupsWithMetadata(
      this.rawConfig?.edit?.project,
      EntityType.Project,
      dataSource,
      this.excludeEditFields
    );
  }

  public getEditCompany(dataSource: string = null): IFieldMetadata[][] {
    return this.fieldGroupsWithMetadata(
      this.rawConfig?.edit?.company,
      EntityType.Company,
      dataSource,
      this.excludeEditFields
    );
  }

  public getEditContact(dataSource: string = null): IFieldMetadata[][] {
    return this.fieldGroupsWithMetadata(
      this.rawConfig?.edit?.contact,
      EntityType.Contact,
      dataSource,
      this.excludeEditFields
    );
  }

  private fieldGroupsWithMetadata(
    fieldGroups: string[][],
    type: EntityType,
    dataSource: string = null,
    denyList: string[] = []
  ): IFieldMetadata[][] {
    if (hasNoElements(fieldGroups)) {
      return [];
    }

    return fieldGroups.map((group) => this.fieldListWithMetadata(group, type, dataSource, denyList));
  }

  private fieldListWithMetadata(
    fieldNameList: string[],
    type: EntityType,
    dataSource: string = null,
    denyList: string[] = []
  ): IFieldMetadata[] {
    if (hasNoElements(fieldNameList)) {
      return [];
    }

    return fieldNameList
      .map<IFieldMetadata>((fieldName) => ({
      ...this.getMetadata(fieldName, type),
      entityType: type,
      fieldName
    }))
      .filter(({ fieldName, validDataSources }) => {
        const isAllowed = !denyList.includes(fieldName);
        if (isNullOrWhiteSpace(dataSource)) {
          return isAllowed;
        }

        return isAllowed && (validDataSources?.includes(dataSource) ?? true);
      });
  }

  public getSupportedCurrencies(): string[] {
    return this.rawConfig?.supportedCurrencies ?? [];
  }

  public getAddress(): IAddress {
    return this.rawConfig?.address;
  }

  public getDescriptions(): string[] {
    return toSafeArray(this.rawConfig?.description ?? `description`);
  }

  public getLoadLatestXMonths(): number {
    return this.rawConfig?.loadLatestXMonths ?? 6;
  }

  DEFAULT_METADATA: IFieldMetadata = { fieldType: `string` };

  public getFieldNameFromUserInput(input: string, type: EntityType): string {
    const meta = this.rawConfig?.metadata?.[type?.toLowerCase() ?? `project`];
    if (meta == null || isNullOrWhiteSpace(input)) {
      return null;
    }

    return Object.keys(meta).find((key) => key.toLowerCase() === input.trim().toLowerCase());
  }

  public getMetadata(fieldName: string, type: EntityType): IFieldMetadata {
    const metadata: IFieldMetadata = {
      ...this.DEFAULT_METADATA,
      entityType: EntityType.Project,
      fieldName,
      ...(this.rawConfig?.metadata?.[type?.toLowerCase() ?? `project`]?.[fieldName] ?? {})
    };

    if (metadata.fieldType === `integer`) {
      metadata.precision = 0;
    }

    if (metadata.fieldType === `decimal`) {
      metadata.precision = 2;
    }

    return metadata;
  }

  public getRequiredFields(type: EntityType): IFieldMetadata[] {
    const metadata: IFieldMetadata[] = this.rawConfig?.metadata?.[type?.toLowerCase() ?? `project`] ?? [];
    return Object.entries(metadata)
      .filter(([, fieldMeta]) => isTrue(fieldMeta?.isRequired, false))
      .map(([fieldName, fieldMeta]) => ({
        ...this.DEFAULT_METADATA,
        entityType: EntityType.Project,
        fieldName,
        ...fieldMeta
      }));
  }

  public getInitialFilterCategory(): string {
    return this.rawConfig?.filterCategories?.initial ?? ``;
  }

  public getFilterCategoryList(): IFilterCategory[] {
    const list = this.rawConfig?.filterCategories?.list;
    if (hasNoElements(list)) {
      return [];
    }

    return list.filter(({ active }) => active ?? false);
  }

  public getToolbar(withOverflow = true): IToolbarItem[] {
    if (hasNoElements(this.rawConfig?.toolbar)) {
      return [];
    }

    if (withOverflow) {
      return this.rawConfig.toolbar.filter((t) => isFalse(t.hide, false));
    }

    return this.rawConfig.toolbar.filter((t) => isFalse(t.isOverflow, false) && isFalse(t.hide, false));
  }

  public getToolbarOverflow(): IToolbarItem[] {
    if (hasNoElements(this.rawConfig?.toolbar)) {
      return [];
    }

    return this.rawConfig.toolbar.filter((t) => isTrue(t.isOverflow, false) && isFalse(t.hide, false));
  }

  private isEnvironmentNameSet = false;

  public setEnvironmentName(): void {
    if (this.isEnvironmentNameSet) {
      return;
    }

    if (typeof window === `undefined`) {
      this.isEnvironmentNameSet = true;
      return;
    }

    const name = this.rawConfig?.environment?.name;
    if (isNullOrWhiteSpace(name)) {
      this.isEnvironmentNameSet = true;
      return;
    }

    if (window?.document?.title?.includes(name)) {
      this.isEnvironmentNameSet = true;
      return;
    }

    window.document.title = `${name} - ${window.document.title}`;
    this.isEnvironmentNameSet = true;
  }

  public getDefaultThemeType(): ThemeType {
    return this.rawConfig?.environment?.theme?.default ?? ThemeType.Auto;
  }

  public getThemeByType(type: ThemeType): PartialTheme {
    const theme: PartialTheme = {
      palette: this.rawConfig?.environment?.theme?.[type]
    };

    const font = this.rawConfig?.environment?.theme?.font;
    if (!isEmpty(font)) {
      theme.defaultFontStyle = font;
    }

    return createTheme(theme);
  }

  public hasCustomLogo(): boolean {
    return isTrue(this.rawConfig?.environment?.logo?.isCustom, false);
  }

  public getLogoDataType(): string {
    switch (this.rawConfig?.environment?.logo?.type) {
      case `png`:
        return `image/png`;
      case `jpg`:
      case `jpeg`:
        return `image/jpeg`;
      case `gif`:
        return `image/gif`;
      case `svg`:
      default:
        return `image/svg+xml`;
    }
  }

  public getLogoPath(type: ThemeType): string {
    if (!this.hasCustomLogo()) {
      return null;
    }
    return `config/custom/logo_${type}.b64`;
  }

  public getProviderSync(): [string, string][] {
    return this.rawConfig?.providerSync ?? [];
  }

  public hasProviderSync(): boolean {
    return hasElements(this.getProviderSync());
  }

  public getContactTileFields(): IFieldMetadata[] {
    if (hasNoElements(this.rawConfig?.detailTiles?.contact.fields)) {
      return [];
    }
    return this.rawConfig.detailTiles.contact.fields
      .map((field) => this.getMetadata(field, EntityType.Contact))
      .slice(0, 4);
  }

  public getDCMergePreference(): "source" | "target" {
    return this.rawConfig?.dcMergePreference ?? `source`;
  }

  public getDCMergeFields(type: EntityType): IMergeField[] {
    if (type == null || type === EntityType.None) {
      return [];
    }

    const entityType = type?.toLowerCase();
    const fields: IMergeField[] = this.rawConfig?.dcMergeFields?.[entityType] ?? [];

    // ensure required fields are present
    const requiredFieldList = DC_MERGE_FIELDS_REQUIRED[entityType];
    for (const requiredField of requiredFieldList) {
      if (!hasElement(fields, (field) => field.fieldName === requiredField)) {
        fields.push({ fieldName: requiredField });
      }
    }

    return fields;
  }

  public isUseSolr(): boolean {
    return isTrue(this.rawConfig?.useSolr, false);
  }

  private static instance: Configuration = null;

  public static async getInstanceAsync(): Promise<Configuration> {
    if (Configuration.instance != null) {
      return Configuration.instance;
    }

    await new Configuration().initialize();
    return Configuration.instance;
  }

  public static getBackendBaseUrl(): string {
    return Configuration.instance.getBackendBaseUrl();
  }

  public static getOptionValues(fieldName: string, type: EntityType): IOptionList {
    return Configuration.instance.getOptionValues(fieldName, type);
  }

  public static getAuth(): IAuth {
    return Configuration.instance.getAuth();
  }

  public static getFilterFieldNames(): string[] {
    return Configuration.instance.getFilterFieldNames();
  }

  public static getOverviewColumns(): IOverviewColumn[] {
    return Configuration.instance.getOverviewColumns();
  }

  public static getDetailsProject(): IFieldMetadata[][] {
    return Configuration.instance.getDetailsProject();
  }

  public static getDetailsCompany(): IFieldMetadata[][] {
    return Configuration.instance.getDetailsCompany();
  }

  public static getDetailsContact(): IFieldMetadata[][] {
    return Configuration.instance.getDetailsContact();
  }

  public static getEditProject(): IFieldMetadata[][] {
    return Configuration.instance.getEditProject();
  }

  public static getEditCompany(): IFieldMetadata[][] {
    return Configuration.instance.getEditCompany();
  }

  public static getEditContact(): IFieldMetadata[][] {
    return Configuration.instance.getEditContact();
  }

  public static getAddress(): IAddress {
    return Configuration.instance.getAddress();
  }

  public static getDescription(): string | string[] {
    return Configuration.instance.getDescriptions();
  }

  public static getLoadLatestXMonths(): number {
    return Configuration.instance.getLoadLatestXMonths();
  }

  public static getSupportedCurrencies(): string[] {
    return Configuration.instance.getSupportedCurrencies();
  }

  public static getMetadata(fieldName: string, entityType: EntityType): IFieldMetadata {
    return Configuration.instance.getMetadata(fieldName, entityType);
  }

  public static getInitialFilterCategory(): string {
    return Configuration.instance.getInitialFilterCategory();
  }

  public static getFilterCategoryList(): IFilterCategory[] {
    return Configuration.instance.getFilterCategoryList();
  }
}
